import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, Select, MenuItem, FormControl,
  InputLabel, FormControlLabel, FormHelperText, Switch, Checkbox, Alert, CircularProgress, Box,
} from '@mui/material';
import { createConnection, updateConnection, testConnection } from '../../api/connections';
import useAuthStore from '../../store/authStore';

const DB_TYPES = [
  { value: 'postgresql', label: 'PostgreSQL', defaultPort: 5432 },
  { value: 'mysql', label: 'MySQL', defaultPort: 3306 },
  { value: 'mssql', label: 'SQL Server', defaultPort: 1433 },
  { value: 'oracle', label: 'Oracle', defaultPort: 1521 },
  { value: 'sqlite', label: 'SQLite', defaultPort: null },
  { value: 'mongodb', label: 'MongoDB', defaultPort: 27017 },
];

const BLANK_FORM = {
  name: '', description: '', db_type: 'postgresql',
  host: 'localhost', port: 5432, database: '',
  username: '', password: '', ssl_enabled: false,
  use_uri: true, connection_string: '', auth_source: '', restricted: false,
};

const ConnectionForm = ({ open, onClose, onSaved, editConnection = null }) => {
  const [form, setForm] = useState(BLANK_FORM);
  const isAdmin = Boolean(useAuthStore((state) => state.user?.is_superuser));
  const isMongo = form.db_type === 'mongodb';
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    if (editConnection) {
      setForm({
        name: editConnection.name || '',
        description: editConnection.description || '',
        db_type: editConnection.db_type || 'postgresql',
        host: editConnection.host || 'localhost',
        port: editConnection.port || '',
        database: editConnection.database || '',
        username: editConnection.username || '',
        password: '', // Don't pre-fill password
        ssl_enabled: editConnection.ssl_enabled || false,
        // The stored URI comes back masked; only send a new one if the user types it
        use_uri: editConnection.db_type === 'mongodb' ? Boolean(editConnection.connection_string) : true,
        connection_string: '',
        auth_source: editConnection.extra_params?.auth_source || '',
        restricted: Boolean(editConnection.restricted),
      });
    } else {
      setForm(BLANK_FORM);
    }
    setError('');
    setTestResult(null);
  }, [editConnection, open]);

  const handleChange = (field) => (e) => {
    const value = ['ssl_enabled', 'use_uri', 'restricted'].includes(field) ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'db_type') {
        const dbType = DB_TYPES.find((t) => t.value === value);
        if (dbType) next.port = dbType.defaultPort || '';
      }
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const { use_uri, connection_string, auth_source, restricted, ...base } = form;
      const payload = { ...base, port: form.port || null };
      // Only admins may set it; others leave it out (new: not restricted, edit: unchanged)
      if (isAdmin) payload.restricted = restricted;
      if (isMongo) {
        const extra = { ...(editConnection?.extra_params || {}) };
        if (use_uri) {
          Object.assign(payload, { host: 'localhost', port: null, username: '', password: '' });
          delete extra.auth_source;
          if (connection_string.trim()) payload.connection_string = connection_string.trim();
        } else if (auth_source.trim()) {
          extra.auth_source = auth_source.trim();
        } else {
          delete extra.auth_source;
        }
        payload.extra_params = extra;
      }
      if (editConnection) {
        // Only send password if user typed a new one
        if (!payload.password) delete payload.password;
        await updateConnection(editConnection.id, payload);
      } else {
        await createConnection(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!editConnection) return;
    setTesting(true);
    setTestResult(null);
    try {
      const result = await testConnection(editConnection.id);
      setTestResult(result);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || err.message });
    } finally {
      setTesting(false);
    }
  };

  const isSQLite = form.db_type === 'sqlite';
  // The backend always prefers a stored URI, so a URI-based connection can't switch to host & port
  const lockedToUri = isMongo && Boolean(editConnection?.connection_string);
  const showUri = isMongo && form.use_uri;
  const showHost = !isSQLite && !showUri;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editConnection ? 'Edit Connection' : 'New Connection'}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {testResult && (
          <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
            {testResult.message}
            {testResult.server_version && ` (${testResult.server_version})`}
          </Alert>
        )}
        <TextField fullWidth label="Name" value={form.name} onChange={handleChange('name')} margin="normal" required />
        <TextField fullWidth label="Description" value={form.description} onChange={handleChange('description')} margin="normal" multiline rows={2} />
        <FormControl fullWidth margin="normal">
          <InputLabel id="db-type-label">Database Type</InputLabel>
          <Select labelId="db-type-label" value={form.db_type} onChange={handleChange('db_type')} label="Database Type">
            {DB_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </Select>
        </FormControl>
        {isMongo && (
          <FormControlLabel
            control={<Switch checked={form.use_uri} onChange={handleChange('use_uri')} disabled={lockedToUri} />}
            label={form.use_uri ? 'Connection string (recommended for Atlas)' : 'Host & port'}
          />
        )}
        {lockedToUri && (
          <FormHelperText>
            This connection uses a connection string. To switch to host &amp; port, create a new connection.
          </FormHelperText>
        )}
        {showUri && (
          <TextField
            fullWidth label="Connection string" value={form.connection_string}
            onChange={handleChange('connection_string')} margin="normal" type="password"
            autoComplete="off"
            placeholder={editConnection ? '(unchanged)' : 'mongodb+srv://user:pass@cluster0.xxxxx.mongodb.net/?retryWrites=true'}
            helperText={lockedToUri ? 'Leave blank to keep the current connection string' : 'For MongoDB Atlas, copy the connection string from Atlas → Connect → Drivers (mongodb+srv://…)'}
          />
        )}
        {showHost && (
          <>
            <TextField fullWidth label="Host" value={form.host} onChange={handleChange('host')} margin="normal" />
            <TextField fullWidth label="Port" value={form.port} onChange={handleChange('port')} margin="normal" type="number" />
          </>
        )}
        <TextField
          fullWidth label={isSQLite ? 'Database Path' : isMongo ? 'Default database' : 'Database'}
          value={form.database} onChange={handleChange('database')} margin="normal" required={!isMongo}
        />
        {showHost && (
          <>
            <TextField fullWidth label="Username" value={form.username} onChange={handleChange('username')} margin="normal" />
            <TextField fullWidth label="Password" value={form.password} onChange={handleChange('password')} margin="normal" type="password" placeholder={editConnection ? '(unchanged)' : ''} />
            {isMongo && (
              <TextField fullWidth label="Auth source" value={form.auth_source} onChange={handleChange('auth_source')} margin="normal" placeholder="admin" />
            )}
            <FormControlLabel control={<Switch checked={form.ssl_enabled} onChange={handleChange('ssl_enabled')} />} label={isMongo ? 'TLS enabled' : 'SSL Enabled'} />
          </>
        )}
        {isAdmin && (
          <>
            <FormControlLabel
              control={<Checkbox checked={form.restricted} onChange={handleChange('restricted')} />}
              label="Admins only (restricted)"
            />
            <FormHelperText>
              Hidden from other users; only pipelines owned by admins (or the built-in examples) can use it.
            </FormHelperText>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {editConnection && (
          <Button onClick={handleTest} disabled={testing}>
            {testing ? <CircularProgress size={20} /> : 'Test'}
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || !form.name}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionForm;
